import { useMemo, useState, useCallback } from 'react';
import { SortOrder } from '@duke-hq/lib/pagination';
import type { BillableRow, BillableDetails, ComparisonData, BillableURLFilters } from './types';
import { ComparisonModal } from './ComparisonModal';
import { BillableDetailsDrawer } from './BillableDetailsDrawer';
import { BillableItemsTable } from './BillableItemsTable';

export function BillableItemsList({
  rows,
  error,
  getDetailsById,
  getProviderName,
  onCompare,
  comparisonData,
  comparisonLoading,
  canManageXeroInvoices,
  urlParams,
  onFilters,
  selectedIdsVisible,
  setSelectedIds,
}: {
  rows: BillableRow[];
  error?: string | null;
  getDetailsById?: (id: string) => BillableDetails | null | undefined;
  getProviderName: (providerId: string) => string | null;
  onCompare: (billableId: string) => void;
  comparisonData: ComparisonData | null | undefined;
  comparisonLoading: boolean;
  canManageXeroInvoices?: boolean;
  urlParams: BillableURLFilters;
  onFilters: (filters: Record<string, string | undefined>, isSort?: boolean) => void;
  selectedIdsVisible: string[];
  setSelectedIds: (ids: string[] | ((prev: string[]) => string[])) => void;
}) {
  // Extract sort params from URL
  const { sort: sortParam, orderBy } = urlParams;
  const sort: SortOrder | undefined =
    sortParam === SortOrder.ASC || sortParam === SortOrder.DESC ? sortParam : undefined;

  // Handle sorting
  const handleSort = useCallback(
    (columnName: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        ...urlParams,
        orderBy: columnName,
        sort: newSortDirection,
      };
      onFilters(updatedFilters, true);
    },
    [onFilters, sort, urlParams],
  );

  const allIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const selectedIdsSet = useMemo(() => new Set(selectedIdsVisible), [selectedIdsVisible]);

  const isAllSelected =
    selectedIdsVisible.length > 0 &&
    allIds.length > 0 &&
    selectedIdsVisible.length === allIds.length;
  const isIndeterminate =
    selectedIdsVisible.length > 0 && allIds.length > 0 && selectedIdsVisible.length < allIds.length;

  const toggleSelectAll = useCallback(() => {
    const setVisible = new Set(allIds);
    setSelectedIds((prev) => {
      if (selectedIdsVisible.length === allIds.length) {
        return prev.filter((id) => !setVisible.has(id));
      }
      const merged = new Set(prev);
      for (const id of allIds) {
        merged.add(id);
      }
      return Array.from(merged);
    });
  }, [allIds, selectedIdsVisible, setSelectedIds]);

  const toggleSelect = useCallback(
    (id: string) =>
      setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [setSelectedIds],
  );

  // ---------- Drawer state ----------
  const [detailId, setDetailId] = useState<string | null>(null);
  const openDetails = useCallback((id: string) => setDetailId(id), []);
  const closeDetails = useCallback(() => setDetailId(null), []);

  // ---------- Comparison modal state ----------
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const openComparison = useCallback(() => setIsComparisonOpen(true), []);
  const closeComparison = useCallback(() => setIsComparisonOpen(false), []);

  const details: BillableDetails | null = useMemo(() => {
    if (detailId == null) {
      return null;
    }
    if (getDetailsById) {
      return getDetailsById(detailId) ?? null;
    }
    const base = rows.find((r) => r.id === detailId);
    return base
      ? {
          ...base,
          providerId: base.providerDrn ?? null,
        }
      : null;
  }, [detailId, getDetailsById, rows]);

  return (
    <>
      <BillableItemsTable
        rows={rows}
        error={error}
        canManageXeroInvoices={canManageXeroInvoices}
        isAllSelected={isAllSelected}
        isIndeterminate={isIndeterminate}
        toggleSelectAll={toggleSelectAll}
        selectedIdsVisible={selectedIdsSet}
        toggleSelect={toggleSelect}
        getProviderName={getProviderName}
        openDetails={openDetails}
        orderBy={orderBy}
        sort={sort}
        onSort={handleSort}
      />

      <BillableDetailsDrawer
        isOpen={detailId != null}
        onClose={closeDetails}
        details={details}
        onOpenComparison={openComparison}
      />

      {typeof detailId === 'string' && detailId.trim() !== '' ? (
        <ComparisonModal
          isOpen={isComparisonOpen}
          onClose={closeComparison}
          billableId={detailId}
          onCompare={onCompare}
          comparisonData={comparisonData}
          isLoading={comparisonLoading}
        />
      ) : null}
    </>
  );
}
